
import { makeAutoObservable, runInAction } from "mobx";
import RootStore from "../../../stores/RootStore";
import ServiceStore from "../../../stores/ServiceStore";
import {
    YardMaps,
    YardLocations,
    YardMapLocationAssignments,
    YardInventoryItem,
    YardMapsData,
    YardMapsPatch,
    YardMapLocationAssignmentsData,
} from "../types/YardTypes";

export class YardMapStore {
    rootStore: RootStore;

    // Loading states
    loading = false;
    error: string | null = null;

    // Selected items for operations
    selectedMapId: number | null = null;
    selectedLocationId: number | null = null;
    selectedAssignmentId: number | null = null;

    // UI state
    viewMode: "view" | "edit" | "assign" = "view";
    mapScale: number = 1;
    mapOffset: { x: number; y: number } = { x: 0, y: 0 };

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        makeAutoObservable(this, { rootStore: false });
    }

    // Service store accessors
    get yardMapsStore(): ServiceStore {
        return this.rootStore.yardMapsStore as ServiceStore;
    }

    get yardLocationsStore(): ServiceStore {
        return this.rootStore.yardLocationsStore as ServiceStore;
    }

    get yardMapLocationAssignmentsStore(): ServiceStore {
        return this.rootStore.yardMapLocationAssignmentsStore as ServiceStore;
    }

    get yardInventoryItemsStore(): ServiceStore {
        return this.rootStore.yardInventoryItemsStore as ServiceStore;
    }

    get yardMovementLogsStore(): ServiceStore {
        return this.rootStore.yardMovementLogsStore as ServiceStore;
    }

    // Computed values
    get yardMaps(): YardMaps[] {
        return this.yardMapsStore.entities as unknown[] as YardMaps[];
    }

    get yardLocations(): YardLocations[] {
        return this.yardLocationsStore.entities as unknown[] as YardLocations[];
    }

    get mapLocationAssignments(): YardMapLocationAssignments[] {
        return this.yardMapLocationAssignmentsStore.entities as unknown[] as YardMapLocationAssignments[];
    }

    get inventoryItems(): YardInventoryItem[] {
        return this.yardInventoryItemsStore.entities as unknown[] as YardInventoryItem[];
    }

    get movementLogs(): any[] {
        return this.yardMovementLogsStore.entities || [];
    }

    get selectedMap(): YardMaps | null {
        if (!this.selectedMapId) return null;
        return this.yardMaps.find(map => map.id === this.selectedMapId) || null;
    }

    get selectedLocation(): YardLocations | null {
        if (!this.selectedLocationId) return null;
        return this.yardLocations.find(loc => loc.id === this.selectedLocationId) || null;
    }

    get selectedAssignment(): YardMapLocationAssignments | null {
        if (!this.selectedAssignmentId) return null;
        return this.mapLocationAssignments.find(assignment => assignment.id === this.selectedAssignmentId) || null;
    }

    get activeMap(): YardMaps | null {
        // Return the first map if none selected, or the selected map
        return this.selectedMap || this.yardMaps[0] || null;
    }

    get currentMapAssignments(): YardMapLocationAssignments[] {
        if (!this.activeMap) return [];
        return this.mapLocationAssignments.filter(assignment => assignment.mapId === this.activeMap!.id);
    }

    get unassignedLocations(): YardLocations[] {
        if (!this.activeMap) return this.yardLocations;
        
        const assignedLocationIds = this.currentMapAssignments.map(assignment => assignment.locationId);
        return this.yardLocations.filter(location => !assignedLocationIds.includes(location.id));
    }

    get mapLocationsWithAssignments() {
        return this.currentMapAssignments.map(assignment => {
            const location = this.yardLocations.find(loc => loc.id === assignment.locationId);
            const inventoryCount = this.inventoryItems.filter(item => item.currentLocationId === assignment.locationId).length;
            
            return {
                ...assignment,
                location,
                inventoryCount,
            };
        });
    }

    // Helper methods
    getLocationAssignment = (locationId: number): YardMapLocationAssignments | null => {
        if (!this.activeMap) return null;
        return this.mapLocationAssignments.find(
            assignment => assignment.mapId === this.activeMap!.id && assignment.locationId === locationId
        ) || null;
    };

    getLocationInventoryCount = (locationId: number): number => {
        return this.inventoryItems.filter(item => item.currentLocationId === locationId).length;
    };

    parseCoordinates = (coordString: string): { x: number; y: number } | null => {
        try {
            const match = coordString.match(/X:(-?\d+(?:\.\d+)?),Y:(-?\d+(?:\.\d+)?)/);
            if (match) {
                return {
                    x: parseFloat(match[1]),
                    y: parseFloat(match[2])
                };
            }
            return null;
        } catch (error) {
            return null;
        }
    };

    formatCoordinates = (x: number, y: number): string => {
        return `X:${x},Y:${y}`;
    };

    // Actions
    setSelectedMap = (mapId: number | null) => {
        this.selectedMapId = mapId;
    };

    setSelectedLocation = (locationId: number | null) => {
        this.selectedLocationId = locationId;
    };

    setSelectedAssignment = (assignmentId: number | null) => {
        this.selectedAssignmentId = assignmentId;
    };

    setViewMode = (mode: "view" | "edit" | "assign") => {
        this.viewMode = mode;
    };

    setMapScale = (scale: number) => {
        this.mapScale = Math.max(0.1, Math.min(5, scale)); // Clamp between 0.1 and 5
    };

    setMapOffset = (offset: { x: number; y: number }) => {
        this.mapOffset = offset;
    };

    setLoading = (loading: boolean) => {
        this.loading = loading;
    };

    setError = (error: string | null) => {
        this.error = error;
    };

    // Async actions
    refreshData = async () => {
        this.setLoading(true);
        this.setError(null);

        try {
            await Promise.all([
                this.yardMapsStore.find({ query: { $limit: 50 } }),
                this.yardLocationsStore.find({ query: { $limit: 100 } }),
                this.yardMapLocationAssignmentsStore.find({ query: { $limit: 200 } }),
                this.yardInventoryItemsStore.find({ query: { $limit: 200 } }),
                this.yardMovementLogsStore.find({ query: { $limit: 50, $sort: { movementDateTime: -1 } } }),
            ]);
        } catch (error) {
            this.setError(error instanceof Error ? error.message : 'Failed to refresh data');
            console.error('Failed to refresh map data:', error);
        } finally {
            this.setLoading(false);
        }
    };

    createMap = async (mapData: YardMapsData) => {
        try {
            const newMap = await this.yardMapsStore.create({
                ...mapData,
                lastUpdated: new Date().toISOString(),
            });

            await this.refreshData();
            return newMap;
        } catch (error) {
            throw new Error(`Failed to create map: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    };

    updateMap = async (mapId: number, updates: YardMapsPatch) => {
        try {
            await this.yardMapsStore.patch(mapId, {
                ...updates,
                lastUpdated: new Date().toISOString(),
            });

            await this.refreshData();
        } catch (error) {
            throw new Error(`Failed to update map: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    };

    deleteMap = async (mapId: number) => {
        try {
            // First, remove all location assignments for this map
            const assignmentsToRemove = this.mapLocationAssignments.filter(assignment => assignment.mapId === mapId);
            
            for (const assignment of assignmentsToRemove) {
                await this.yardMapLocationAssignmentsStore.remove(assignment.id);
            }

            // Then remove the map
            await this.yardMapsStore.remove(mapId);

            await this.refreshData();
        } catch (error) {
            throw new Error(`Failed to delete map: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    };

    assignLocationToMap = async (
        mapId: number,
        locationId: number,
        coordinates: string
    ) => {
        try {
            // Check if location is already assigned to this map
            const existingAssignment = this.mapLocationAssignments.find(
                assignment => assignment.mapId === mapId && assignment.locationId === locationId
            );

            if (existingAssignment) {
                // Update existing assignment
                await this.yardMapLocationAssignmentsStore.patch(existingAssignment.id, {
                    mapCoordinates: coordinates,
                });
            } else {
                // Create new assignment
                await this.yardMapLocationAssignmentsStore.create({
                    mapId,
                    locationId,
                    mapCoordinates: coordinates,
                });
            }

            await this.refreshData();
        } catch (error) {
            throw new Error(`Failed to assign location to map: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    };

    removeLocationAssignment = async (assignmentId: number) => {
        try {
            await this.yardMapLocationAssignmentsStore.remove(assignmentId);
            await this.refreshData();
        } catch (error) {
            throw new Error(`Failed to remove location assignment: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    };

    updateLocationAssignment = async (
        assignmentId: number,
        coordinates: string
    ) => {
        try {
            await this.yardMapLocationAssignmentsStore.patch(assignmentId, {
                mapCoordinates: coordinates,
            });

            await this.refreshData();
        } catch (error) {
            throw new Error(`Failed to update location assignment: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    };

    // Map interaction methods
    zoomIn = () => {
        this.setMapScale(this.mapScale * 1.2);
    };

    zoomOut = () => {
        this.setMapScale(this.mapScale / 1.2);
    };

    resetView = () => {
        this.setMapScale(1);
        this.setMapOffset({ x: 0, y: 0 });
    };

    panMap = (deltaX: number, deltaY: number) => {
        this.setMapOffset({
            x: this.mapOffset.x + deltaX,
            y: this.mapOffset.y + deltaY,
        });
    };

    // Inventory movement methods
    moveInventoryItem = async (itemId: number, toLocationId: number) => {
        try {
            const item = this.inventoryItems.find(i => i.id === itemId);
            if (!item) throw new Error('Inventory item not found');

            const fromLocationId = item.currentLocationId;

            // Update inventory item location
            await this.yardInventoryItemsStore.patch(itemId, {
                currentLocationId: toLocationId,
            });

            // Create movement log
            await this.yardMovementLogsStore.create({
                inventoryItemId: itemId,
                fromLocationId: fromLocationId,
                toLocationId: toLocationId,
                movedByUserId: (this.rootStore as any).userStore?.currentUser?.id || 1,
                quantityMoved: item.quantity,
                notes: `Moved via Yard Map`,
            });

            await this.refreshData();
        } catch (error) {
            throw new Error(`Failed to move inventory: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    };

    getLocationMovementHistory = (locationId: number): any[] => {
        return this.movementLogs.filter(
            log => log.fromLocationId === locationId || log.toLocationId === locationId
        ).sort((a, b) => new Date(b.movementDateTime).getTime() - new Date(a.movementDateTime).getTime());
    };

    getInventoryMovementHistory = (inventoryItemId: number): any[] => {
        return this.movementLogs.filter(
            log => log.inventoryItemId === inventoryItemId
        ).sort((a, b) => new Date(b.movementDateTime).getTime() - new Date(a.movementDateTime).getTime());
    };

    clear = () => {
        runInAction(() => {
            this.selectedMapId = null;
            this.selectedLocationId = null;
            this.selectedAssignmentId = null;
            this.viewMode = "view";
            this.mapScale = 1;
            this.mapOffset = { x: 0, y: 0 };
            this.error = null;
            this.loading = false;
        });
    };
}

export default YardMapStore;
